/** @jsxImportSource @emotion/react */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { WHITE } from '../../styles/colors';
import { LAYER } from '../zIndex';
import { FADE_IN_ANIMATION_SHORT } from '../../styles/animations';
import { doNothing } from '../../language/module';
import { PortalTargetContext } from './portalTarget';


const Background = styled('div')`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  ${FADE_IN_ANIMATION_SHORT};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${LAYER.TWO.MODAL_BASE};
`;

const mainModalCss = (mainCss?: SerializedStyles) => css`
  max-height: calc(100% - 32px);
  max-width: calc(100% - 32px);
  cursor: default;
  overflow-x: hidden;
  overflow-y: auto;
  border-radius: 8px;
  background-color: ${WHITE};
  transition: all .5s ease;
  ${mainCss}
`;


/**
 * Modal: a component that shows a dark background over the screen and its children in a white div with all the
 * nice modal stylings.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class Modal extends React.PureComponent<{
  title?: string;
  dismissable?: boolean,
  onDismiss?: () => void,
  children: React.ReactNode | React.ReactNode[];
  rootCss?: SerializedStyles;
  disableClickoffDismissal?: boolean;
  disableKeypressDismissal?: boolean;
}> {
  public static defaultProps = {
    dismissable: true,
  };

  // Lets a detached window (see layout/window/) redirect this portal into its own
  // document; falls back to the main window when unset.
  public static contextType = PortalTargetContext;


  public componentDidMount() {
    document.addEventListener('keyup', this.onKeyUp); // Idempotent
  }


  public componentWillUnmount() {
    document.removeEventListener('keyup', this.onKeyUp); // Idempotent
  }


  private onDismissModal = () =>  this.props.dismissable && !this.props.disableClickoffDismissal!
                                  ? (this.props.onDismiss || doNothing)()
                                  : doNothing();


  private onKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && !this.props.disableKeypressDismissal) {
      this.onDismissModal();
    }
  };


  private stopPropagation = (event: React.MouseEvent) => event && event.stopPropagation();


  public render = () => ReactDOM.createPortal(
    <Background aria-modal='true' onClick={this.onDismissModal}>
      <div title={this.props.title || ''}
           onClick={this.stopPropagation}
           css={mainModalCss(this.props.rootCss)}>
        {this.props.children}
      </div>
    </Background>,
    ((this.context as Document | null) ?? document).body,
  );
}
